import { SelectOption } from '~/shared/utils/types';

export const AUTOSCALING_STATE_PARAM_KEY = 'autoscaling_state';
export const AUTOSCALING_ELIGIBILITY_PARAM_KEY = 'autoscaling_eligibility';

export const LEGACY_UNSUPPORTED_STATE_ID = 'as_state_unsupported';
export const LEGACY_UNSUPPORTED_API_VALUE = 'not_supported';

export const AUTOSCALING_STATE_UI_IDS = ['as_state_on', 'as_state_off'] as const;
export const AUTOSCALING_ELIGIBILITY_UI_IDS = [
  'as_eligibility_supported',
  'as_eligibility_potential',
  'as_eligibility_unsupported',
] as const;

export type AutoscalingStateUiId = (typeof AUTOSCALING_STATE_UI_IDS)[number];
export type AutoscalingEligibilityUiId = (typeof AUTOSCALING_ELIGIBILITY_UI_IDS)[number];

export const autoscaleStateToFilterMap: Record<string, SelectOption | null> = {
  as_state_on: { id: 'on', name: 'Active', group: 'autoscalingState' },
  as_state_off: { id: 'off', name: 'Inactive', group: 'autoscalingState' },
};

export const eligibilityToFilterMap: Record<string, SelectOption | null> = {
  as_eligibility_supported: { id: 'true', name: 'Supported', group: 'supported' },
  as_eligibility_potential: { id: 'potential', name: 'Pending Setup', group: 'supported' },
  as_eligibility_unsupported: { id: 'false', name: 'Unsupported', group: 'supported' },
};

export function createAutoscalingStateFilters(stateIds: string[]): SelectOption[] {
  return stateIds
    .map((state) => autoscaleStateToFilterMap[state])
    .filter((f): f is SelectOption => f != null);
}

export function createEligibilityFilters(eligibilityIds: string[]): SelectOption[] {
  return eligibilityIds
    .map((id) => eligibilityToFilterMap[id])
    .filter((f): f is SelectOption => f != null);
}

export function isAutoscalingStateUiId(id: string): id is AutoscalingStateUiId {
  return (AUTOSCALING_STATE_UI_IDS as readonly string[]).includes(id);
}

export function isAutoscalingEligibilityUiId(id: string): id is AutoscalingEligibilityUiId {
  return (AUTOSCALING_ELIGIBILITY_UI_IDS as readonly string[]).includes(id);
}

export function countAutoscalingFilterSelections(
  stateIds: string[],
  eligibilityIds: string[]
): number {
  const stateCount = stateIds.filter(isAutoscalingStateUiId).length;
  const eligibilityCount = eligibilityIds.filter(isAutoscalingEligibilityUiId).length;
  return stateCount + eligibilityCount;
}

export interface MigratedAutoscalingUrlParams {
  stateIds: string[];
  eligibilityIds: string[];
  didMigrate: boolean;
}

export function migrateLegacyAutoscalingUrlParams(
  stateIds: string[],
  eligibilityIds: string[],
  apiFilters: SelectOption[] = []
): MigratedAutoscalingUrlParams {
  let migratedStateIds = [...stateIds];
  let migratedEligibilityIds = [...eligibilityIds];
  let didMigrate = false;

  if (migratedStateIds.includes(LEGACY_UNSUPPORTED_STATE_ID)) {
    migratedStateIds = migratedStateIds.filter((id) => id !== LEGACY_UNSUPPORTED_STATE_ID);
    if (!migratedEligibilityIds.includes('as_eligibility_unsupported')) {
      migratedEligibilityIds.push('as_eligibility_unsupported');
    }
    didMigrate = true;
  }

  const hasLegacyUnsupportedApiFilter = apiFilters.some(
    (filter) =>
      filter.group === 'autoscalingState' &&
      (filter.id === LEGACY_UNSUPPORTED_API_VALUE || filter.id.includes(LEGACY_UNSUPPORTED_API_VALUE))
  );

  if (hasLegacyUnsupportedApiFilter) {
    if (!migratedEligibilityIds.includes('as_eligibility_unsupported')) {
      migratedEligibilityIds.push('as_eligibility_unsupported');
    }
    didMigrate = true;
  }

  return {
    stateIds: migratedStateIds,
    eligibilityIds: migratedEligibilityIds,
    didMigrate,
  };
}

export function stripAutoscalingFiltersFromApiFilters(filters: SelectOption[]): SelectOption[] {
  return filters.filter(
    (filter) => filter.group !== 'autoscalingState' && filter.group !== 'supported'
  );
}
